import { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Instagram,
  Github,
  Copy,
  Check,
  ArrowUp,
  ExternalLink,
} from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Contact() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const emailAddress = "sarangseykol@gmail.com";

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(emailAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section
      id="contact"
      className="pt-20 pb-28 md:pb-16 px-6 sm:px-12 lg:px-20 bg-neutral-50/60 dark:bg-black text-neutral-900 dark:text-white relative border-t border-neutral-200/60 dark:border-neutral-900"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="inline-block py-1 px-3 rounded-full bg-neutral-200/80 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-300 text-xs font-semibold tracking-wider uppercase mb-3 border border-neutral-300/80 dark:border-neutral-800">
            {t("contact.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-neutral-900 dark:text-white">
            {t("contact.title")}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 max-w-xl mx-auto text-base sm:text-lg">
            {t("contact.desc")}
          </p>
        </motion.div>

        {/* Contact Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {/* Email Card with Copy Feature */}
          <div className="md:col-span-3 lg:col-span-1 p-6 rounded-2xl bg-white dark:bg-neutral-950/70 border border-neutral-200/80 dark:border-neutral-800/80 shadow-sm flex flex-col justify-between">
            <div>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-blue-500/10 text-blue-600 dark:text-blue-400 mb-4 border border-blue-500/20">
                <Mail size={20} />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                {t("contact.emailMe")}
              </h3>
              <p className="text-sm font-semibold text-neutral-900 dark:text-white break-all mb-4">
                {emailAddress}
              </p>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={handleCopyEmail}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-neutral-100 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-colors"
              >
                {copied ? (
                  <>
                    <Check size={14} className="text-emerald-500" />
                    <span>{t("contact.copied")}</span>
                  </>
                ) : (
                  <>
                    <Copy size={14} />
                    <span>{t("contact.copyEmail")}</span>
                  </>
                )}
              </button>

              <a
                href={`mailto:${emailAddress}`}
                className="inline-flex items-center justify-center px-3 py-2 rounded-xl text-xs font-semibold bg-neutral-950 text-white dark:bg-white dark:text-neutral-950 hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors"
                title={t("contact.openMail")}
              >
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* GitHub Card */}
          <a
            href="https://github.com/Rangsey0"
            target="_blank"
            rel="noopener noreferrer"
            className="group p-6 rounded-2xl bg-white dark:bg-neutral-950/70 border border-neutral-200/80 dark:border-neutral-800/80 shadow-sm hover:border-neutral-300 dark:hover:border-neutral-700 transition-all flex flex-col justify-between"
          >
            <div>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 mb-4 border border-neutral-200/80 dark:border-neutral-800 group-hover:scale-105 transition-transform">
                <Github size={20} />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                {t("contact.github")}
              </h3>
              <p className="text-sm font-semibold text-neutral-900 dark:text-white mb-2">
                @Rangsey0
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Explore open source repositories & code.
              </p>
            </div>

            <div className="pt-4 flex items-center justify-between text-xs font-semibold text-neutral-600 dark:text-neutral-400 group-hover:text-blue-500 transition-colors">
              <span>View Profile</span>
              <ExternalLink size={13} />
            </div>
          </a>

          {/* Instagram Card */}
          <a
            href="https://www.instagram.com/rangsey0/"
            target="_blank"
            rel="noopener noreferrer"
            className="group p-6 rounded-2xl bg-white dark:bg-neutral-950/70 border border-neutral-200/80 dark:border-neutral-800/80 shadow-sm hover:border-neutral-300 dark:hover:border-neutral-700 transition-all flex flex-col justify-between"
          >
            <div>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-pink-500/10 text-pink-600 dark:text-pink-400 mb-4 border border-pink-500/20 group-hover:scale-105 transition-transform">
                <Instagram size={20} />
              </div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                {t("contact.instagram")}
              </h3>
              <p className="text-sm font-semibold text-neutral-900 dark:text-white mb-2">
                @rangsey0
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Personal updates, design notes & life.
              </p>
            </div>

            <div className="pt-4 flex items-center justify-between text-xs font-semibold text-neutral-600 dark:text-neutral-400 group-hover:text-blue-500 transition-colors">
              <span>Follow</span>
              <ExternalLink size={13} />
            </div>
          </a>
        </div>

        {/* Minimal Footer */}
        <footer className="pt-8 border-t border-neutral-200 dark:border-neutral-900 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-500 dark:text-neutral-400">
          <p className="font-medium">
            © {new Date().getFullYear()} Rangsey. {t("contact.copyright")}
          </p>

          <div className="flex items-center gap-4">
            <button
              onClick={scrollToTop}
              aria-label={t("contact.backToTop") || "Back to top"}
              className="inline-flex items-center gap-1 p-2 rounded-xl bg-neutral-100 dark:bg-neutral-900 hover:bg-neutral-200 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 transition-colors"
            >
              <ArrowUp size={14} />
            </button>
          </div>
        </footer>
      </div>
    </section>
  );
}
